import React from 'react';
import {
  Loader,
  Icon,
  Table,
  Button,
  Header,
  Label,
  Checkbox,
  Dimmer,
} from 'semantic-ui-react';
import moment from 'moment';
import * as ExcelJS from 'exceljs/dist/exceljs.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Line,
  Tooltip as TooltipChart,
  ComposedChart,
  Area,
} from 'recharts';

import SelectAssetInReport from '../SelectAssetInReport';
import { alertMessage } from '../../../helper/alert-message';
import { reportView } from '../helper/dataService';
import { getIdsAndTime, getDuration } from '../helper/helper';
import reportsEnv from '../reportsEnv';
import { getData, handleMultipleRequest } from '../../../helper/dataService';
import { digitalInputValueFormat } from '../../../helper/digital-input-value-format';
import { REGULAR_PATH } from '../../../../env';

const { durationLimit, assetLimit } = reportsEnv.temperatureC;

class CustomTooltip extends React.Component {
  render() {
    const { active, payload } = this.props;

    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: 'white',
            padding: 10,
            border: '1px solid #D3D3D3',
          }}
        >
          <p>{payload[0].payload.time}</p>

          {payload.map((data) => {
            const hasDigitalInput =
              data.dataKey === 'digitalInput1' ||
              data.dataKey === 'digitalInput2' ||
              data.dataKey === 'digitalInput3';

            let isReversed = false;
            const inputName = data.dataKey;
            if (inputName === 'digitalInput1') {
              isReversed = data.payload.digitalInput1Reversed;
            } else if (inputName === 'digitalInput2') {
              isReversed = data.payload.digitalInput2Reversed;
            } else if (inputName === 'digitalInput3') {
              isReversed = data.payload.digitalInput3Reversed;
            }

            let hasValue;
            if (isReversed) {
              hasValue = data.value === 0;
            } else {
              hasValue = data.value === 50;
            }

            const label = digitalInputValueFormat(
              data.name,
              hasValue,
              isReversed,
            );

            return (
              <p style={{ color: data.stroke }} key={data.name}>
                <span>{data.name}</span>: {hasDigitalInput ? label : data.value}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  }
}

class TemperatureC extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      active: false,
      pdfDisableBtn: false,
      excelDisableBtn: false,
      assetsList: [],
      selectedAssets: [],
      data: [],
      startTime: moment().startOf('day'),
      endTime: moment().endOf('day'),
      graphList: [],
      temperature1: false,
      temperature2: false,
      temperature3: false,
      temperature4: false,
      temperature5: false,
      temperature6: false,
      temperature7: false,
      temperature8: false,
      digitalInput1: false,
      digitalInput2: false,
      digitalInput3: false,
      assetsWithTemperature: [],
      digitalInput1Reversed: false,
      digitalInput2Reversed: false,
      digitalInput3Reversed: false,
      digitalInput1Name: '',
      digitalInput2Name: '',
      digitalInput3Name: '',
      enableDigitalInput1: false,
      enableDigitalInput2: false,
      enableDigitalInput3: false,
      digitalInput1Active: false,
      digitalInput2Active: false,
      digitalInput3Active: false,
      lastDataLoading: false,
    };
  }

  componentDidMount() {
    const {
      selectedAssets = [],
      startTime,
      endTime,
    } = JSON.parse(window.localStorage.getItem('reports_state'));

    this.setState({
      selectedAssets,
      startTime: moment(startTime),
      endTime: moment(endTime),
    });
  }

  getBack = () => {
    this.setState({
      data: [],
    });
  };

  downloadReport = () => {
    this.setState({
      active: true,
      pdfDisableBtn: false,
      excelDisableBtn: false,
    });
    const { ids, startTime, endTime } = getIdsAndTime(this.state);

    let promises = [];

    for (let i = 0; i < ids.length; i++) {
      let id = ids[i];
      let url = `${REGULAR_PATH}/assets/${id}/last_data?access_token=`;

      promises.push(getData(url));
    }

    this.setState({ lastDataLoading: true });

    let lastDataResponse = handleMultipleRequest(promises);

    lastDataResponse
      .then((results) => {
        results.forEach((res) => {
          if (
            res.data.temperature_sensor_2_name ||
            res.data.temperature_sensor_3_name ||
            res.data.temperature_sensor_4_name ||
            res.data.temperature_sensor_5_name ||
            res.data.temperature_sensor_6_name ||
            res.data.temperature_sensor_7_name ||
            res.data.temperature_sensor_8_name ||
            res.data.temperature_sensor_name ||
            res.data.digital_input_sensor_1_name ||
            res.data.digital_input_sensor_2_name ||
            res.data.digital_input_sensor_3_name
          ) {
            const asset = {
              name: res.data.name,
              temperature1Name: res.data.temperature_sensor_name || '',
              temperature2Name: res.data.temperature_sensor_2_name || '',
              temperature3Name: res.data.temperature_sensor_3_name || '',
              temperature4Name: res.data.temperature_sensor_4_name || '',
              temperature5Name: res.data.temperature_sensor_5_name || '',
              temperature6Name: res.data.temperature_sensor_6_name || '',
              temperature7Name: res.data.temperature_sensor_7_name || '',
              temperature8Name: res.data.temperature_sensor_8_name || '',
              digitalInput1Name: res.data.digital_input_sensor_1_name || '',
              digitalInput2Name: res.data.digital_input_sensor_2_name || '',
              digitalInput3Name: res.data.digital_input_sensor_3_name || '',
            };

            if (res.data.digital_input_sensor_1 === 'True') {
              this.setState({ digitalInput1: true });
            }
            if (res.data.digital_input_sensor_2 === 'True') {
              this.setState({ digitalInput2: true });
            }
            if (res.data.digital_input_sensor_3 === 'True') {
              this.setState({ digitalInput3: true });
            }

            this.setState((prevState) => ({
              assetsWithTemperature: [
                ...prevState.assetsWithTemperature,
                asset,
              ],
            }));

            this.setState({
              digitalInput1Name: res.data.digital_input_sensor_1_name || '',
              digitalInput2Name: res.data.digital_input_sensor_2_name || '',
              digitalInput3Name: res.data.digital_input_sensor_3_name || '',
            });

            this.setState({
              digitalInput1Reversed:
                res.data.digital_input_sensor_1_reversed === 'True'
                  ? true
                  : false,
            });
            this.setState({
              digitalInput2Reversed:
                res.data.digital_input_sensor_2_reversed === 'True'
                  ? true
                  : false,
            });
            this.setState({
              digitalInput3Reversed:
                res.data.digital_input_sensor_3_reversed === 'True'
                  ? true
                  : false,
            });
          }
        });
        this.setState({ lastDataLoading: false });
      })
      .catch((error) => {
        this.setState({ lastDataLoading: false });
      });

    Promise.all([
      reportView(reportsEnv.temperatureC, {
        asset_ids: ids,
        from: startTime,
        to: endTime,
      }),
      reportView(reportsEnv.temperature, {
        asset_ids: ids,
        from: startTime,
        to: endTime,
      }),
    ])
      .then(([diResponse, tempResponse]) => {
        const temp = this.mergeReportData(
          (diResponse && diResponse.data) || [],
          (tempResponse && tempResponse.data) || [],
        );

        const assetListWithTemperature = [...this.state.assetsWithTemperature];

        assetListWithTemperature.forEach((asset) => {
          const index = temp.findIndex(
            (d) => d.asset_detail.name === asset.name,
          );
          if (index > -1) {
            temp[index].temperature1Name = asset.temperature1Name;
            temp[index].temperature2Name = asset.temperature2Name;
            temp[index].temperature3Name = asset.temperature3Name;
            temp[index].temperature4Name = asset.temperature4Name;
            temp[index].temperature5Name = asset.temperature5Name;
            temp[index].temperature6Name = asset.temperature6Name;
            temp[index].temperature7Name = asset.temperature7Name;
            temp[index].temperature8Name = asset.temperature8Name;
            temp[index].digitalInput1Name = asset.digitalInput1Name;
            temp[index].digitalInput2Name = asset.digitalInput2Name;
            temp[index].digitalInput3Name = asset.digitalInput3Name;
          }
        });

        const convertToGraphData = (hasValue, isReversed) => {
          if (hasValue) {
            return isReversed ? 0 : 50;
          } else {
            return isReversed ? 50 : 0;
          }
        };

        const graphList = temp.map((asset) => {
          let filteredList = [];
          const assetData = asset.data || asset.temperature_data || [];

          assetData.forEach((item) => {
            const {
              temperature,
              temperature_2,
              temperature_3,
              temperature_4,
              temperature_5,
              temperature_6,
              temperature_7,
              temperature_8,
              digital_input_1,
              digital_input_2,
              digital_input_3,
            } = item;

            let graphData = {};
            if (temperature) {
              graphData = {
                ...graphData,
                temperature1: temperature,
              };
              this.setState({ temperature1: true });
            }
            if (temperature_2) {
              graphData = {
                ...graphData,
                temperature2: temperature_2,
              };
              this.setState({ temperature2: true });
            }
            if (temperature_3) {
              graphData = {
                ...graphData,
                temperature3: temperature_3,
              };
              this.setState({ temperature3: true });
            }
            if (temperature_4) {
              graphData = {
                ...graphData,
                temperature4: temperature_4,
              };
              this.setState({ temperature4: true });
            }
            if (temperature_5) {
              graphData = {
                ...graphData,
                temperature5: temperature_5,
              };
              this.setState({ temperature5: true });
            }
            if (temperature_6) {
              graphData = {
                ...graphData,
                temperature6: temperature_6,
              };
              this.setState({ temperature6: true });
            }
            if (temperature_7) {
              graphData = {
                ...graphData,
                temperature7: temperature_7,
              };
              this.setState({ temperature7: true });
            }
            if (temperature_8) {
              graphData = {
                ...graphData,
                temperature8: temperature_8,
              };
              this.setState({ temperature8: true });
            }

            if (digital_input_1) {
              this.setState({ digitalInput1Active: true });
            }
            if (digital_input_2) {
              this.setState({ digitalInput2Active: true });
            }
            if (digital_input_3) {
              this.setState({ digitalInput3Active: true });
            }

            filteredList.push({
              time: moment(item.time.$date).format('DD/MM/YY, hh:mm:ss a'),
              digitalInput1: convertToGraphData(
                item.digital_input_1,
                this.state.digitalInput1Reversed,
              ),
              digitalInput2: convertToGraphData(
                item.digital_input_2,
                this.state.digitalInput2Reversed,
              ),
              digitalInput3: convertToGraphData(
                item.digital_input_3,
                this.state.digitalInput3Reversed,
              ),
              digitalInput1Reversed: this.state.digitalInput1Reversed,
              digitalInput2Reversed: this.state.digitalInput2Reversed,
              digitalInput3Reversed: this.state.digitalInput3Reversed,

              ...graphData,
            });
          });
          return filteredList;
        });

        this.setState({
          data: temp,
          graphList,
          active: false,
        });
      })
      .catch((error) => {
        this.setState({ active: false });
        alertMessage(`Sorry! Something went wrong`);
      });
  };

  mergeReportData = (diData = [], tempData = []) => {
    return diData.map((asset) => {
      const matchingTempAsset = tempData.find(
        (tAsset) =>
          tAsset.asset_detail &&
          asset.asset_detail &&
          tAsset.asset_detail.name === asset.asset_detail.name,
      );

      const tempMap = new Map();
      if (
        matchingTempAsset &&
        (matchingTempAsset.temperature_data || matchingTempAsset.data)
      ) {
        const records =
          matchingTempAsset.temperature_data ||
          matchingTempAsset.data ||
          [];
        records.forEach((record) => {
          if (record.time && record.time.$date) {
            tempMap.set(record.time.$date, record);
          }
        });
      }

      const mergedData = (asset.data || []).map((item) => {
        let matchingRecord =
          item.time && item.time.$date
            ? tempMap.get(item.time.$date)
            : null;

        if (!matchingRecord && item.time && item.time.$date) {
          const itemTime = item.time.$date;
          for (let [t, rec] of tempMap.entries()) {
            if (Math.abs(t - itemTime) <= 2000) {
              matchingRecord = rec;
              break;
            }
          }
        }

        return {
          ...item,
          speed:
            matchingRecord && matchingRecord.speed !== undefined
              ? matchingRecord.speed
              : item.speed !== undefined
              ? item.speed
              : 0,
          alarm_type:
            matchingRecord && matchingRecord.alarm_type !== undefined
              ? matchingRecord.alarm_type
              : item.alarm_type,
          location:
            matchingRecord && matchingRecord.location
              ? matchingRecord.location
              : item.location,
        };
      });

      return {
        ...asset,
        data: mergedData,
      };
    });
  };

  downloadPDF = (data, startTime, endTime) => {
    const doc = new jsPDF('landscape');

    doc.setFontSize(16);
    doc.text('Finder GPS Tracking', 14, 15);

    doc.setFontSize(12);
    doc.text('Temperature Report', 14, 22);

    doc.setFontSize(9);
    doc.text(
      `Generated on: ${moment().format('MMMM Do, YYYY, hh:mm:ss A')}`,
      14,
      28,
    );
    doc.text(
      `From: ${moment(startTime).format('dddd, MMMM Do, YYYY, hh:mm:ss A')}`,
      14,
      34,
    );
    doc.text(
      `Till: ${moment(endTime).format('dddd, MMMM Do, YYYY, hh:mm:ss A')}`,
      14,
      40,
    );

    let startY = 46;

    data.forEach((asset, index) => {
      if (index > 0) {
        doc.addPage();
        startY = 15;
      }

      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(`Asset Name: ${asset.asset_detail.name}`, 14, startY);
      doc.setFont(undefined, 'normal');

      const headers = ['Time'];
      if (this.state.temperature1)
        headers.push(`${asset.temperature1Name || 'Temp 1'} (°C)`);
      if (this.state.temperature2)
        headers.push(`${asset.temperature2Name || 'Temp 2'} (°C)`);
      if (this.state.temperature3)
        headers.push(`${asset.temperature3Name || 'Temp 3'} (°C)`);
      if (this.state.temperature4)
        headers.push(`${asset.temperature4Name || 'Temp 4'} (°C)`);
      if (this.state.temperature5)
        headers.push(`${asset.temperature5Name || 'Temp 5'} (°C)`);
      if (this.state.temperature6)
        headers.push(`${asset.temperature6Name || 'Temp 6'} (°C)`);
      if (this.state.temperature7)
        headers.push(`${asset.temperature7Name || 'Temp 7'} (°C)`);
      if (this.state.temperature8)
        headers.push(`${asset.temperature8Name || 'Temp 8'} (°C)`);
      if (this.state.digitalInput1)
        headers.push(asset.digitalInput1Name || 'Digital Indicator 1');
      if (this.state.digitalInput2)
        headers.push(asset.digitalInput2Name || 'Digital Indicator 2');
      if (this.state.digitalInput3)
        headers.push(asset.digitalInput3Name || 'Digital Indicator 3');
      headers.push('Speed (km/h)', 'Ignition Status', 'Location');

      const rows = [];
      const assetData = asset.data || asset.temperature_data || [];
      assetData.forEach((item) => {
        if (
          item.temperature ||
          item.temperature_2 ||
          item.temperature_3 ||
          item.temperature_4 ||
          item.temperature_5 ||
          item.temperature_6 ||
          item.temperature_7 ||
          item.temperature_8
        ) {
          const row = [
            item.time
              ? moment(item.time.$date).format(
                  'dddd, MMMM Do, YYYY, hh:mm:ss A',
                )
              : '',
          ];

          if (this.state.temperature1) row.push(item.temperature || '-');
          if (this.state.temperature2) row.push(item.temperature_2 || '-');
          if (this.state.temperature3) row.push(item.temperature_3 || '-');
          if (this.state.temperature4) row.push(item.temperature_4 || '-');
          if (this.state.temperature5) row.push(item.temperature_5 || '-');
          if (this.state.temperature6) row.push(item.temperature_6 || '-');
          if (this.state.temperature7) row.push(item.temperature_7 || '-');
          if (this.state.temperature8) row.push(item.temperature_8 || '-');

          if (this.state.digitalInput1) {
            row.push(
              digitalInputValueFormat(
                this.state.digitalInput1Name,
                item.digital_input_1,
                this.state.digitalInput1Reversed,
              ),
            );
          }
          if (this.state.digitalInput2) {
            row.push(
              digitalInputValueFormat(
                this.state.digitalInput2Name,
                item.digital_input_2,
                this.state.digitalInput2Reversed,
              ),
            );
          }
          if (this.state.digitalInput3) {
            row.push(
              digitalInputValueFormat(
                this.state.digitalInput3Name,
                item.digital_input_3,
                this.state.digitalInput3Reversed,
              ),
            );
          }

          row.push(item.speed || 0);
          row.push(
            item.alarm_type !== null ? (item.alarm_type ? 'ON' : 'OFF') : '',
          );

          const lat =
            item.location &&
            item.location.coordinates &&
            item.location.coordinates.length === 2
              ? item.location.coordinates[1].toFixed(6)
              : null;
          const lng =
            item.location &&
            item.location.coordinates &&
            item.location.coordinates.length === 2
              ? item.location.coordinates[0].toFixed(6)
              : null;

          row.push(lat && lng ? `${lat}, ${lng}` : '-');
          rows.push(row);
        }
      });

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: startY + 4,
        theme: 'grid',
        headStyles: {
          fillColor: [211, 211, 211],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
        },
        styles: {
          fontSize: 7.5,
          cellPadding: 2,
        },
      });

      if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
        startY = doc.lastAutoTable.finalY + 10;
      }
    });

    doc.save(
      `Finder_Temperature_Report_${moment().format('DD-MMMM-YYYY_hh:mm A')}.pdf`,
    );
  };

  handleDownloadPdfBtn = () => {
    const { data, startTime, endTime } = this.state;

    if (data.length > 0) {
      this.downloadPDF(data, startTime, endTime);
      return;
    }

    this.setState({ pdfDisableBtn: true });

    const {
      ids,
      startTime: startTimeUnix,
      endTime: endTimeUnix,
    } = getIdsAndTime(this.state);

    let promises = [];

    for (let i = 0; i < ids.length; i++) {
      let id = ids[i];
      let url = `${REGULAR_PATH}/assets/${id}/last_data?access_token=`;

      promises.push(getData(url));
    }

    let lastDataResponse = handleMultipleRequest(promises);

    lastDataResponse
      .then((results) => {
        const assetsWithTemperature = [];
        let di1 = false,
          di2 = false,
          di3 = false;
        let di1Name = '',
          di2Name = '',
          di3Name = '';
        let di1Rev = false,
          di2Rev = false,
          di3Rev = false;

        results.forEach((res) => {
          if (
            res.data.temperature_sensor_2_name ||
            res.data.temperature_sensor_3_name ||
            res.data.temperature_sensor_4_name ||
            res.data.temperature_sensor_5_name ||
            res.data.temperature_sensor_6_name ||
            res.data.temperature_sensor_7_name ||
            res.data.temperature_sensor_8_name ||
            res.data.temperature_sensor_name ||
            res.data.digital_input_sensor_1_name ||
            res.data.digital_input_sensor_2_name ||
            res.data.digital_input_sensor_3_name
          ) {
            const asset = {
              name: res.data.name,
              temperature1Name: res.data.temperature_sensor_name || '',
              temperature2Name: res.data.temperature_sensor_2_name || '',
              temperature3Name: res.data.temperature_sensor_3_name || '',
              temperature4Name: res.data.temperature_sensor_4_name || '',
              temperature5Name: res.data.temperature_sensor_5_name || '',
              temperature6Name: res.data.temperature_sensor_6_name || '',
              temperature7Name: res.data.temperature_sensor_7_name || '',
              temperature8Name: res.data.temperature_sensor_8_name || '',
              digitalInput1Name: res.data.digital_input_sensor_1_name || '',
              digitalInput2Name: res.data.digital_input_sensor_2_name || '',
              digitalInput3Name: res.data.digital_input_sensor_3_name || '',
            };
            assetsWithTemperature.push(asset);

            if (res.data.digital_input_sensor_1 === 'True') di1 = true;
            if (res.data.digital_input_sensor_2 === 'True') di2 = true;
            if (res.data.digital_input_sensor_3 === 'True') di3 = true;
            if (res.data.digital_input_sensor_1_name)
              di1Name = res.data.digital_input_sensor_1_name;
            if (res.data.digital_input_sensor_2_name)
              di2Name = res.data.digital_input_sensor_2_name;
            if (res.data.digital_input_sensor_3_name)
              di3Name = res.data.digital_input_sensor_3_name;
            if (res.data.digital_input_sensor_1_reversed === 'True')
              di1Rev = true;
            if (res.data.digital_input_sensor_2_reversed === 'True')
              di2Rev = true;
            if (res.data.digital_input_sensor_3_reversed === 'True')
              di3Rev = true;
          }
        });

        Promise.all([
          reportView(reportsEnv.temperatureC, {
            asset_ids: ids,
            from: startTimeUnix,
            to: endTimeUnix,
          }),
          reportView(reportsEnv.temperature, {
            asset_ids: ids,
            from: startTimeUnix,
            to: endTimeUnix,
          }),
        ])
          .then(([diResponse, tempResponse]) => {
            const temp = this.mergeReportData(
              (diResponse && diResponse.data) || [],
              (tempResponse && tempResponse.data) || [],
            );

            assetsWithTemperature.forEach((asset) => {
              const index = temp.findIndex(
                (d) => d.asset_detail.name === asset.name,
              );
              if (index > -1) {
                temp[index].temperature1Name = asset.temperature1Name;
                temp[index].temperature2Name = asset.temperature2Name;
                temp[index].temperature3Name = asset.temperature3Name;
                temp[index].temperature4Name = asset.temperature4Name;
                temp[index].temperature5Name = asset.temperature5Name;
                temp[index].temperature6Name = asset.temperature6Name;
                temp[index].temperature7Name = asset.temperature7Name;
                temp[index].temperature8Name = asset.temperature8Name;
                temp[index].digitalInput1Name = asset.digitalInput1Name;
                temp[index].digitalInput2Name = asset.digitalInput2Name;
                temp[index].digitalInput3Name = asset.digitalInput3Name;
              }
            });

            let t1 = false,
              t2 = false,
              t3 = false,
              t4 = false,
              t5 = false,
              t6 = false,
              t7 = false,
              t8 = false;
            temp.forEach((asset) => {
              const assetData = asset.data || asset.temperature_data || [];
              assetData.forEach((item) => {
                if (item.temperature) t1 = true;
                if (item.temperature_2) t2 = true;
                if (item.temperature_3) t3 = true;
                if (item.temperature_4) t4 = true;
                if (item.temperature_5) t5 = true;
                if (item.temperature_6) t6 = true;
                if (item.temperature_7) t7 = true;
                if (item.temperature_8) t8 = true;
              });
            });

            this.setState(
              {
                temperature1: t1,
                temperature2: t2,
                temperature3: t3,
                temperature4: t4,
                temperature5: t5,
                temperature6: t6,
                temperature7: t7,
                temperature8: t8,
                digitalInput1: di1,
                digitalInput2: di2,
                digitalInput3: di3,
                digitalInput1Name: di1Name,
                digitalInput2Name: di2Name,
                digitalInput3Name: di3Name,
                digitalInput1Reversed: di1Rev,
                digitalInput2Reversed: di2Rev,
                digitalInput3Reversed: di3Rev,
              },
              () => {
                this.downloadPDF(temp, startTime, endTime);
                this.setState({ pdfDisableBtn: false });
              },
            );
          })
          .catch((error) => {
            this.setState({ pdfDisableBtn: false });
            alertMessage(`Sorry! Something went wrong`);
          });
      })
      .catch((error) => {
        this.setState({ pdfDisableBtn: false });
        alertMessage(`Sorry! Something went wrong`);
      });
  };

  handleDownloadExcelBtn = () => {
    const { data, startTime, endTime } = this.state;

    if (data.length > 0) {
      this.downloadExcelJS(data, startTime, endTime);
      return;
    }

    this.setState({ excelDisableBtn: true });

    const {
      ids,
      startTime: startTimeUnix,
      endTime: endTimeUnix,
    } = getIdsAndTime(this.state);

    let promises = [];

    for (let i = 0; i < ids.length; i++) {
      let id = ids[i];
      let url = `${REGULAR_PATH}/assets/${id}/last_data?access_token=`;

      promises.push(getData(url));
    }

    let lastDataResponse = handleMultipleRequest(promises);

    lastDataResponse
      .then((results) => {
        const assetsWithTemperature = [];
        let di1 = false,
          di2 = false,
          di3 = false;
        let di1Name = '',
          di2Name = '',
          di3Name = '';
        let di1Rev = false,
          di2Rev = false,
          di3Rev = false;

        results.forEach((res) => {
          if (
            res.data.temperature_sensor_2_name ||
            res.data.temperature_sensor_3_name ||
            res.data.temperature_sensor_4_name ||
            res.data.temperature_sensor_5_name ||
            res.data.temperature_sensor_6_name ||
            res.data.temperature_sensor_7_name ||
            res.data.temperature_sensor_8_name ||
            res.data.temperature_sensor_name ||
            res.data.digital_input_sensor_1_name ||
            res.data.digital_input_sensor_2_name ||
            res.data.digital_input_sensor_3_name
          ) {
            const asset = {
              name: res.data.name,
              temperature1Name: res.data.temperature_sensor_name || '',
              temperature2Name: res.data.temperature_sensor_2_name || '',
              temperature3Name: res.data.temperature_sensor_3_name || '',
              temperature4Name: res.data.temperature_sensor_4_name || '',
              temperature5Name: res.data.temperature_sensor_5_name || '',
              temperature6Name: res.data.temperature_sensor_6_name || '',
              temperature7Name: res.data.temperature_sensor_7_name || '',
              temperature8Name: res.data.temperature_sensor_8_name || '',
              digitalInput1Name: res.data.digital_input_sensor_1_name || '',
              digitalInput2Name: res.data.digital_input_sensor_2_name || '',
              digitalInput3Name: res.data.digital_input_sensor_3_name || '',
            };
            assetsWithTemperature.push(asset);

            if (res.data.digital_input_sensor_1 === 'True') di1 = true;
            if (res.data.digital_input_sensor_2 === 'True') di2 = true;
            if (res.data.digital_input_sensor_3 === 'True') di3 = true;
            if (res.data.digital_input_sensor_1_name)
              di1Name = res.data.digital_input_sensor_1_name;
            if (res.data.digital_input_sensor_2_name)
              di2Name = res.data.digital_input_sensor_2_name;
            if (res.data.digital_input_sensor_3_name)
              di3Name = res.data.digital_input_sensor_3_name;
            if (res.data.digital_input_sensor_1_reversed === 'True')
              di1Rev = true;
            if (res.data.digital_input_sensor_2_reversed === 'True')
              di2Rev = true;
            if (res.data.digital_input_sensor_3_reversed === 'True')
              di3Rev = true;
          }
        });

        Promise.all([
          reportView(reportsEnv.temperatureC, {
            asset_ids: ids,
            from: startTimeUnix,
            to: endTimeUnix,
          }),
          reportView(reportsEnv.temperature, {
            asset_ids: ids,
            from: startTimeUnix,
            to: endTimeUnix,
          }),
        ])
          .then(([diResponse, tempResponse]) => {
            const temp = this.mergeReportData(
              (diResponse && diResponse.data) || [],
              (tempResponse && tempResponse.data) || [],
            );

            assetsWithTemperature.forEach((asset) => {
              const index = temp.findIndex(
                (d) => d.asset_detail.name === asset.name,
              );
              if (index > -1) {
                temp[index].temperature1Name = asset.temperature1Name;
                temp[index].temperature2Name = asset.temperature2Name;
                temp[index].temperature3Name = asset.temperature3Name;
                temp[index].temperature4Name = asset.temperature4Name;
                temp[index].temperature5Name = asset.temperature5Name;
                temp[index].temperature6Name = asset.temperature6Name;
                temp[index].temperature7Name = asset.temperature7Name;
                temp[index].temperature8Name = asset.temperature8Name;
                temp[index].digitalInput1Name = asset.digitalInput1Name;
                temp[index].digitalInput2Name = asset.digitalInput2Name;
                temp[index].digitalInput3Name = asset.digitalInput3Name;
              }
            });

            let t1 = false,
              t2 = false,
              t3 = false,
              t4 = false,
              t5 = false,
              t6 = false,
              t7 = false,
              t8 = false;
            temp.forEach((asset) => {
              const assetData = asset.data || asset.temperature_data || [];
              assetData.forEach((item) => {
                if (item.temperature) t1 = true;
                if (item.temperature_2) t2 = true;
                if (item.temperature_3) t3 = true;
                if (item.temperature_4) t4 = true;
                if (item.temperature_5) t5 = true;
                if (item.temperature_6) t6 = true;
                if (item.temperature_7) t7 = true;
                if (item.temperature_8) t8 = true;
              });
            });

            this.setState(
              {
                temperature1: t1,
                temperature2: t2,
                temperature3: t3,
                temperature4: t4,
                temperature5: t5,
                temperature6: t6,
                temperature7: t7,
                temperature8: t8,
                digitalInput1: di1,
                digitalInput2: di2,
                digitalInput3: di3,
                digitalInput1Name: di1Name,
                digitalInput2Name: di2Name,
                digitalInput3Name: di3Name,
                digitalInput1Reversed: di1Rev,
                digitalInput2Reversed: di2Rev,
                digitalInput3Reversed: di3Rev,
              },
              () => {
                this.downloadExcelJS(temp, startTime, endTime);
                this.setState({ excelDisableBtn: false });
              },
            );
          })
          .catch((error) => {
            this.setState({ excelDisableBtn: false });
            alertMessage(`Sorry! Something went wrong`);
          });
      })
      .catch((error) => {
        this.setState({ excelDisableBtn: false });
        alertMessage(`Sorry! Something went wrong`);
      });
  };

  downloadExcelJS = (data, startTime, endTime) => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Finder GPS Tracking';
    workbook.created = new Date();

    workbook.addWorksheet('Temperature Report');

    const worksheet = workbook.getWorksheet('Temperature Report');

    worksheet.columns = [
      { width: 35 }, // Time
      { width: 25 }, // Temp 1
      { width: 25 }, // Temp 2
      { width: 25 }, // Temp 3
      { width: 25 }, // Temp 4
      { width: 25 }, // Temp 5
      { width: 25 }, // Temp 6
      { width: 25 }, // Temp 7
      { width: 25 }, // Temp 8
      { width: 25 }, // Digital Input 1
      { width: 25 }, // Digital Input 2
      { width: 25 }, // Digital Input 3
      { width: 15 }, // Speed
      { width: 15 }, // Ignition
      { width: 30 }, // Location
    ];

    worksheet.addRow(['Finder GPS Tracking']);
    worksheet.addRow(['Temperature Report']);
    worksheet.addRow([
      `Generated on ${moment().format('MMMM Do, YYYY, hh:mm:ss A')}`,
    ]);

    worksheet.addRow();
    worksheet.addRow([
      `From: ${moment(startTime).format('dddd, MMMM Do, YYYY, hh:mm:ss A')}`,
    ]);
    worksheet.addRow([
      `Till: ${moment(endTime).format('dddd, MMMM Do, YYYY, hh:mm:ss A')}`,
    ]);

    worksheet.addRow();

    data.forEach((asset) => {
      worksheet.addRow([`Asset Name: ${asset.asset_detail.name}`]);
      worksheet.addRow();

      const headers = ['Time'];
      if (this.state.temperature1)
        headers.push(`${asset.temperature1Name} (°C)`);
      if (this.state.temperature2)
        headers.push(`${asset.temperature2Name} (°C)`);
      if (this.state.temperature3)
        headers.push(`${asset.temperature3Name} (°C)`);
      if (this.state.temperature4)
        headers.push(`${asset.temperature4Name} (°C)`);
      if (this.state.temperature5)
        headers.push(`${asset.temperature5Name} (°C)`);
      if (this.state.temperature6)
        headers.push(`${asset.temperature6Name} (°C)`);
      if (this.state.temperature7)
        headers.push(`${asset.temperature7Name} (°C)`);
      if (this.state.temperature8)
        headers.push(`${asset.temperature8Name} (°C)`);
      if (this.state.digitalInput1)
        headers.push(asset.digitalInput1Name || 'Digital Indicator 1');
      if (this.state.digitalInput2)
        headers.push(asset.digitalInput2Name || 'Digital Indicator 2');
      if (this.state.digitalInput3)
        headers.push(asset.digitalInput3Name || 'Digital Indicator 3');
      headers.push('Speed (km/h)', 'Ignition Status', 'Location');

      const headerRow = worksheet.addRow(headers);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' },
        };
        cell.font = {
          bold: true,
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });

      const assetData = asset.data || asset.temperature_data || [];
      assetData.forEach((item) => {
        if (
          item.temperature ||
          item.temperature_2 ||
          item.temperature_3 ||
          item.temperature_4 ||
          item.temperature_5 ||
          item.temperature_6 ||
          item.temperature_7 ||
          item.temperature_8
        ) {
          const row = [
            item.time
              ? moment(item.time.$date).format(
                  'dddd, MMMM Do, YYYY, hh:mm:ss A',
                )
              : '',
          ];

          if (this.state.temperature1) row.push(item.temperature || '-');
          if (this.state.temperature2) row.push(item.temperature_2 || '-');
          if (this.state.temperature3) row.push(item.temperature_3 || '-');
          if (this.state.temperature4) row.push(item.temperature_4 || '-');
          if (this.state.temperature5) row.push(item.temperature_5 || '-');
          if (this.state.temperature6) row.push(item.temperature_6 || '-');
          if (this.state.temperature7) row.push(item.temperature_7 || '-');
          if (this.state.temperature8) row.push(item.temperature_8 || '-');

          if (this.state.digitalInput1) {
            row.push(
              digitalInputValueFormat(
                this.state.digitalInput1Name,
                item.digital_input_1,
                this.state.digitalInput1Reversed,
              ),
            );
          }
          if (this.state.digitalInput2) {
            row.push(
              digitalInputValueFormat(
                this.state.digitalInput2Name,
                item.digital_input_2,
                this.state.digitalInput2Reversed,
              ),
            );
          }
          if (this.state.digitalInput3) {
            row.push(
              digitalInputValueFormat(
                this.state.digitalInput3Name,
                item.digital_input_3,
                this.state.digitalInput3Reversed,
              ),
            );
          }

          row.push(item.speed || 0);
          row.push(
            item.alarm_type !== null ? (item.alarm_type ? 'ON' : 'OFF') : '',
          );

          const lat =
            item.location &&
            item.location.coordinates &&
            item.location.coordinates.length === 2
              ? item.location.coordinates[1].toFixed(6)
              : null;
          const lng =
            item.location &&
            item.location.coordinates &&
            item.location.coordinates.length === 2
              ? item.location.coordinates[0].toFixed(6)
              : null;

          const newRow = worksheet.addRow(row);
          const cell = newRow.getCell(row.length + 1);

          if (lat && lng) {
            cell.value = {
              text: `${lat}, ${lng}`,
              hyperlink: `https://www.google.com/maps?q=${lat},${lng}`,
              tooltip: 'View on Google Maps',
            };
            cell.font = {
              color: { argb: 'FF0000FF' },
              underline: true,
            };
          } else {
            cell.value = '-';
          }
        }
      });

      worksheet.addRow();
      worksheet.addRow();
    });

    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: 'application/octet-binary',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Finder_Temperature_Report_${moment().format('DD-MMMM-YYYY_hh:mm A')}.xlsx`;
      a.click();
      a.remove();
    });
  };

  handleCheck = (_, data) => {
    const { checked, label } = data;
    if (label === this.state.digitalInput1Name) {
      this.setState({ enableDigitalInput1: checked });
    } else if (label === this.state.digitalInput2Name) {
      this.setState({ enableDigitalInput2: checked });
    } else {
      this.setState({ enableDigitalInput3: checked });
    }
  };

  render() {
    const {
      selectedAssets,
      data,
      active,
      pdfDisableBtn,
      excelDisableBtn,
      startTime,
      endTime,
      graphList,
      temperature1,
      temperature2,
      temperature3,
      temperature4,
      temperature5,
      temperature6,
      temperature7,
      temperature8,
      digitalInput1,
      digitalInput2,
      digitalInput3,
      digitalInput1Reversed,
      digitalInput2Reversed,
      digitalInput3Reversed,
      digitalInput1Name,
      digitalInput2Name,
      digitalInput3Name,
      enableDigitalInput1,
      enableDigitalInput2,
      enableDigitalInput3,
      digitalInput1Active,
      digitalInput2Active,
      digitalInput3Active,
      lastDataLoading,
    } = this.state;

    const duration = getDuration(endTime, startTime);
    const assetsQuantity = selectedAssets.length;

    const isDurationLimitCross = duration > durationLimit;
    const isAssetLimitCross = assetsQuantity > assetLimit;

    if (isDurationLimitCross) {
      alertMessage(
        `You can't select this report more than ${durationLimit} days.`,
      );
    } else if (isAssetLimitCross) {
      alertMessage(
        `You can't select this report more than ${assetLimit} assets.`,
      );
    }

    const title = `Temperature [C] [${durationLimit} days][${assetLimit} asset]`;
    const isValidDuration =
      duration > 0 && endTime.isSameOrAfter(startTime) && !isDurationLimitCross;
    const isValidAsset = assetsQuantity > 0 && !isAssetLimitCross;

    if (lastDataLoading) {
      return (
        <Dimmer active={true}>
          <Loader indeterminate size='big'>
            Loading...
          </Loader>
        </Dimmer>
      );
    }

    return (
      <div>
        {!active && data.length <= 0 && (
          <div className=' center-report'>
            <Header as='h2'>{title}</Header>

            <div style={{ margin: '20px 0' }}>
              <Label.Group size='large'>
                <Label>
                  <Icon name='car' />
                  {assetsQuantity} assets
                </Label>
                <Label>
                  <Icon name='calendar' />
                  {duration} days
                </Label>
                <Label>
                  <Icon name='play' />
                  {moment(startTime).format('dddd, MMMM Do, YYYY, hh:mm:ss A')}
                </Label>
                <Label>
                  <Icon name='stop' />
                  {moment(endTime).format('dddd, MMMM Do, YYYY, hh:mm:ss A')}
                </Label>
              </Label.Group>
            </div>

            {isValidDuration && isValidAsset && (
              <Button.Group>
                <Button
                  color='black'
                  style={{ width: '200px', fontSize: '16px' }}
                  onClick={this.downloadReport}
                  icon='eye'
                  content='VIEW HERE'
                  disabled={pdfDisableBtn || excelDisableBtn}
                />

                <Button
                  color='teal'
                  style={{ width: '200px', fontSize: '16px' }}
                  icon='file pdf outline'
                  content='Download PDF'
                  onClick={this.handleDownloadPdfBtn}
                  disabled={pdfDisableBtn || excelDisableBtn}
                  loading={pdfDisableBtn}
                />

                <Button
                  color='teal'
                  style={{ width: '200px', fontSize: '16px' }}
                  icon='file excel outline'
                  content='Download Excel'
                  onClick={this.handleDownloadExcelBtn}
                  disabled={excelDisableBtn || pdfDisableBtn}
                  loading={excelDisableBtn}
                />
              </Button.Group>
            )}
          </div>
        )}

        {!active && data.length > 0 ? (
          <div style={{ margin: '2%' }}>
            <div className='left-right'>
              <div>
                <Header as='h2'>Temperature [C]</Header>
                <p>
                  Generated on: {moment().format('MMMM Do, YYYY, hh:mm:ss A')}
                </p>
              </div>
              <div>
                <Button
                  color='teal'
                  onClick={() => this.downloadPDF(data, startTime, endTime)}
                  style={{ marginRight: '10px' }}
                >
                  Download PDF
                </Button>
                <Button
                  color='teal'
                  onClick={() => this.downloadExcelJS(data, startTime, endTime)}
                  style={{ marginRight: '30px' }}
                >
                  Download Excel
                </Button>
                <Button primary onClick={this.getBack}>
                  Back
                </Button>
              </div>
            </div>

            <br />

            {data.map((item, i) => (
              <div key={i} id={item.asset_detail.name}>
                <div className='ui stackable grid'>
                  <div className='eight wide column' style={{ paddingLeft: 0 }}>
                    <div className='ui card fluid'>
                      <div className='content'>
                        <div className='header'>{item.asset_detail.name}</div>

                        <div className='description'>
                          <div>
                            <p>
                              <i className='arrow right icon' /> From:
                              {moment(startTime).format(
                                'dddd, MMMM Do, YYYY, hh:mm:ss A',
                              )}
                            </p>
                            <p>
                              <i className='arrow right icon' /> Till:{' '}
                              {moment(endTime).format(
                                'dddd, MMMM Do, YYYY, hh:mm:ss A',
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className='eight wide column'
                    style={{ paddingRight: 0 }}
                  >
                    <div className='ui card fluid'>
                      <div className='content'>
                        <h2 className='ui center aligned icon header'>
                          <i className='circular car icon' />
                        </h2>
                        {!(item.data || item.temperature_data || []).some(
                          (data) =>
                            data.temperature ||
                            data.temperature_2 ||
                            data.temperature_3 ||
                            data.temperature_4 ||
                            data.temperature_5 ||
                            data.temperature_6 ||
                            data.temperature_7 ||
                            data.temperature_8,
                        ) ? (
                          <h4
                            style={{
                              textAlign: 'center',
                              fontSize: 20,
                            }}
                          >
                            Temperature Sensor Not Connected
                          </h4>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                {(item.data || item.temperature_data || []).some(
                  (data) =>
                    data.temperature ||
                    data.temperature_2 ||
                    data.temperature_3 ||
                    data.temperature_4 ||
                    data.temperature_5 ||
                    data.temperature_6 ||
                    data.temperature_7 ||
                    data.temperature_8,
                ) ? (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: 10,
                      }}
                    >
                      <div style={{ display: 'flex', gap: 15 }}>
                        {digitalInput1Active ? (
                          <Checkbox
                            checked={enableDigitalInput1}
                            label={digitalInput1Name}
                            onChange={this.handleCheck}
                          />
                        ) : null}

                        {digitalInput2Active ? (
                          <Checkbox
                            checked={enableDigitalInput2}
                            label={digitalInput2Name}
                            onChange={this.handleCheck}
                          />
                        ) : null}

                        {digitalInput3Active ? (
                          <Checkbox
                            checked={enableDigitalInput3}
                            label={digitalInput3Name}
                            onChange={this.handleCheck}
                          />
                        ) : null}
                      </div>
                    </div>

                    {graphList[i] && graphList[i].length > 1 ? (
                      <ResponsiveContainer height={200} width='100%'>
                        <ComposedChart
                          data={graphList[i]}
                          margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid strokeDasharray='3 3' />
                          <XAxis dataKey='time' />
                          <YAxis />
                          <TooltipChart content={<CustomTooltip />} />
                          <Legend />
                          {temperature1 ? (
                            <Line
                              type='monotone'
                              dataKey='temperature1'
                              stroke='#ef5675'
                              name={
                                `${data[i].temperature1Name} (°C)` ||
                                'Temperature 1'
                              }
                              dot={false}
                            />
                          ) : null}
                          {temperature2 ? (
                            <Line
                              type='monotone'
                              dataKey='temperature2'
                              stroke='#FFA38F'
                              name={
                                `${data[i].temperature2Name} (°C)` ||
                                'Temperature 2'
                              }
                              dot={false}
                            />
                          ) : null}
                          {temperature3 ? (
                            <Line
                              type='monotone'
                              dataKey='temperature3'
                              stroke='#36C2CE'
                              name={
                                `${data[i].temperature3Name} (°C)` ||
                                'Temperature 3'
                              }
                              dot={false}
                            />
                          ) : null}
                          {temperature4 ? (
                            <Line
                              type='monotone'
                              dataKey='temperature4'
                              stroke='#4A249D'
                              name={
                                `${data[i].temperature4Name} (°C)` ||
                                'Temperature 4'
                              }
                              dot={false}
                            />
                          ) : null}
                          {temperature5 ? (
                            <Line
                              type='monotone'
                              dataKey='temperature5'
                              stroke='#4A249D'
                              name={
                                `${data[i].temperature5Name} (°C)` ||
                                'Temperature 5'
                              }
                              dot={false}
                            />
                          ) : null}
                          {temperature6 ? (
                            <Line
                              type='monotone'
                              dataKey='temperature6'
                              stroke='#4A249D'
                              name={
                                `${data[i].temperature6Name} (°C)` ||
                                'Temperature 6'
                              }
                              dot={false}
                            />
                          ) : null}
                          {temperature7 ? (
                            <Line
                              type='monotone'
                              dataKey='temperature7'
                              stroke='#4A249D'
                              name={
                                `${data[i].temperature7Name} (°C)` ||
                                'Temperature 7'
                              }
                              dot={false}
                            />
                          ) : null}
                          {temperature8 ? (
                            <Line
                              type='monotone'
                              dataKey='temperature8'
                              stroke='#4A249D'
                              name={
                                `${data[i].temperature8Name} (°C)` ||
                                'Temperature 8'
                              }
                              dot={false}
                            />
                          ) : null}
                          {enableDigitalInput1 ? (
                            <Area
                              type='monotone'
                              dataKey='digitalInput1'
                              fill='#e5e5f6'
                              stroke='#8884d8'
                              name={
                                `${data[i].digitalInput1Name}` ||
                                'Digital Indicator 1'
                              }
                            />
                          ) : null}

                          {enableDigitalInput2 ? (
                            <Area
                              type='monotone'
                              dataKey='digitalInput2'
                              fill='#e2ff4b'
                              stroke='#8884d8'
                              name={
                                `${data[i].digitalInput2Name}` ||
                                'Digital Indicator 2'
                              }
                            />
                          ) : null}

                          {enableDigitalInput3 ? (
                            <Area
                              type='monotone'
                              dataKey='digitalInput3'
                              fill='#f4c4cc'
                              stroke='#8884d8'
                              name={
                                `${data[i].digitalInput3Name}` ||
                                'Digital Indicator 3'
                              }
                            />
                          ) : null}
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : null}
                    <Table celled>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Time</Table.HeaderCell>
                          {temperature1 ? (
                            <Table.HeaderCell>
                              {`${item.temperature1Name} (°C)` ||
                                'Temperature 1(°C)'}
                            </Table.HeaderCell>
                          ) : null}
                          {temperature2 ? (
                            <Table.HeaderCell>
                              {`${item.temperature2Name} (°C)` ||
                                'Temperature 2(°C)'}
                            </Table.HeaderCell>
                          ) : null}
                          {temperature3 ? (
                            <Table.HeaderCell>
                              {`${item.temperature3Name} (°C)` ||
                                'Temperature 3(°C)'}
                            </Table.HeaderCell>
                          ) : null}
                          {temperature4 ? (
                            <Table.HeaderCell>
                              {`${item.temperature4Name} (°C)` ||
                                'Temperature 4(°C)'}
                            </Table.HeaderCell>
                          ) : null}
                          {temperature5 ? (
                            <Table.HeaderCell>
                              {`${item.temperature5Name} (°C)` ||
                                'Temperature 5(°C)'}
                            </Table.HeaderCell>
                          ) : null}
                          {temperature6 ? (
                            <Table.HeaderCell>
                              {`${item.temperature6Name} (°C)` ||
                                'Temperature 6(°C)'}
                            </Table.HeaderCell>
                          ) : null}
                          {temperature7 ? (
                            <Table.HeaderCell>
                              {`${item.temperature7Name} (°C)` ||
                                'Temperature 7(°C)'}
                            </Table.HeaderCell>
                          ) : null}
                          {temperature8 ? (
                            <Table.HeaderCell>
                              {`${item.temperature8Name} (°C)` ||
                                'Temperature 8(°C)'}
                            </Table.HeaderCell>
                          ) : null}
                          {digitalInput1 ? (
                            <Table.HeaderCell>
                              {item.digitalInput1Name || 'Digital Indicator 1'}
                            </Table.HeaderCell>
                          ) : null}
                          {digitalInput2 ? (
                            <Table.HeaderCell>
                              {item.digitalInput2Name || 'Digital Indicator 2'}
                            </Table.HeaderCell>
                          ) : null}
                          {digitalInput3 ? (
                            <Table.HeaderCell>
                              {item.digitalInput3Name || 'Digital Indicator 3'}
                            </Table.HeaderCell>
                          ) : null}
                          <Table.HeaderCell>Speed (km/h)</Table.HeaderCell>
                          <Table.HeaderCell>Ignition Status</Table.HeaderCell>
                          <Table.HeaderCell>Location</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {(item.data || item.temperature_data || []).map(
                          (itemData, id) => {
                            if (
                              itemData.temperature ||
                              itemData.temperature_2 ||
                              itemData.temperature_3 ||
                              itemData.temperature_4 ||
                              itemData.temperature_5 ||
                              itemData.temperature_6 ||
                              itemData.temperature_7 ||
                              itemData.temperature_8
                            ) {
                              return (
                                <Table.Row key={id}>
                                  <Table.Cell>
                                    {itemData.time
                                      ? moment(itemData.time.$date).format(
                                          'dddd, MMMM Do, YYYY, hh:mm:ss A',
                                        )
                                      : ''}
                                  </Table.Cell>
                                  {temperature1 ? (
                                    <Table.Cell>
                                      {itemData.temperature || '-'}
                                    </Table.Cell>
                                  ) : null}
                                  {temperature2 ? (
                                    <Table.Cell>
                                      {itemData.temperature_2 || '-'}
                                    </Table.Cell>
                                  ) : null}
                                  {temperature3 ? (
                                    <Table.Cell>
                                      {itemData.temperature_3 || '-'}
                                    </Table.Cell>
                                  ) : null}
                                  {temperature4 ? (
                                    <Table.Cell>
                                      {itemData.temperature_4 || '-'}
                                    </Table.Cell>
                                  ) : null}
                                  {temperature5 ? (
                                    <Table.Cell>
                                      {itemData.temperature_5 || '-'}
                                    </Table.Cell>
                                  ) : null}
                                  {temperature6 ? (
                                    <Table.Cell>
                                      {itemData.temperature_6 || '-'}
                                    </Table.Cell>
                                  ) : null}
                                  {temperature7 ? (
                                    <Table.Cell>
                                      {itemData.temperature_7 || '-'}
                                    </Table.Cell>
                                  ) : null}
                                  {temperature8 ? (
                                    <Table.Cell>
                                      {itemData.temperature_8 || '-'}
                                    </Table.Cell>
                                  ) : null}

                                  {digitalInput1 ? (
                                    <Table.Cell>
                                      {digitalInputValueFormat(
                                        digitalInput1Name,
                                        itemData.digital_input_1,
                                        digitalInput1Reversed,
                                      )}
                                    </Table.Cell>
                                  ) : null}
                                  {digitalInput2 ? (
                                    <Table.Cell>
                                      {digitalInputValueFormat(
                                        digitalInput2Name,
                                        itemData.digital_input_2,
                                        digitalInput2Reversed,
                                      )}
                                    </Table.Cell>
                                  ) : null}
                                  {digitalInput3 ? (
                                    <Table.Cell>
                                      {digitalInputValueFormat(
                                        digitalInput3Name,
                                        itemData.digital_input_3,
                                        digitalInput3Reversed,
                                      )}
                                    </Table.Cell>
                                  ) : null}

                                  <Table.Cell>
                                    {itemData.speed || 0}
                                  </Table.Cell>

                                  <Table.Cell>
                                    {itemData.alarm_type !== null
                                      ? itemData.alarm_type
                                        ? 'ON'
                                        : 'OFF'
                                      : ''}
                                  </Table.Cell>

                                  <Table.Cell>
                                    {itemData.location &&
                                    itemData.location.coordinates &&
                                    itemData.location.coordinates.length ===
                                      2 ? (
                                      <a
                                        href={`https://www.google.com/maps?q=${itemData.location.coordinates[1]},${itemData.location.coordinates[0]}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                      >
                                        {`${itemData.location.coordinates[1].toFixed(
                                          6,
                                        )}, ${itemData.location.coordinates[0].toFixed(
                                          6,
                                        )}`}
                                        <Icon
                                          name='external alternate'
                                          style={{ marginLeft: '5px' }}
                                        />
                                      </a>
                                    ) : (
                                      '-'
                                    )}
                                  </Table.Cell>
                                </Table.Row>
                              );
                            }

                            return null;
                          },
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                ) : null}

                <br />
              </div>
            ))}
            {data.length > 1 ? <SelectAssetInReport assetList={data} /> : null}
          </div>
        ) : (
          <Loader active={active} inline='centered'>
            Generating...
          </Loader>
        )}
      </div>
    );
  }
}

export default TemperatureC;
